/**
 * Modal Global de Soporte por WhatsApp — WhatsAppSoporteModal
 * Componente transversal y reutilizable para cualquier módulo de AviFácil.
 * Permite iniciar conversación directa con el canal oficial de asistencia técnica y zootécnica.
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Copy, Check, MessageSquare, ExternalLink } from 'lucide-react';
import { WhatsAppIcon } from '@/shared/components/ui/icons/WhatsAppIcon';
import styles from './WhatsAppSoporteModal.module.css';

export interface WhatsAppSoporteModalProps {
  isOpen: boolean;
  onClose: () => void;
  numeroWhatsApp?: string; // Ej: "+573000000000"
  numeroDisplay?: string;  // Ej: "+57 (300) 000-0000"
  mensajeInicial?: string;
  contexto?: string;       // Ej: "Login", "Registro", "Granja"
}

export function WhatsAppSoporteModal({
  isOpen,
  onClose,
  numeroWhatsApp = '573000000000',
  numeroDisplay = '+57 300 000 0000',
  mensajeInicial,
  contexto = 'General',
}: WhatsAppSoporteModalProps) {
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mensaje, setMensaje] = useState(
    mensajeInicial ||
      `Hola AviFácil, necesito asistencia con la plataforma (${contexto}).`
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mensajeInicial) {
      setMensaje(mensajeInicial);
    } else {
      setMensaje(`Hola AviFácil, necesito asistencia con la plataforma (${contexto}).`);
    }
  }, [mensajeInicial, contexto]);

  // Cerrar con Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleCopyNumber = () => {
    navigator.clipboard.writeText(numeroDisplay);
    setCopied(true);
    toast.success('Número de WhatsApp copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const getWhatsAppUrl = () => {
    const cleanNumber = numeroWhatsApp.replace(/\D/g, '');
    const encodedMsg = encodeURIComponent(mensaje.trim());
    return `https://wa.me/${cleanNumber}?text=${encodedMsg}`;
  };

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wpp-modal-title"
    >
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Encabezado */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconBadge}>
              <WhatsAppIcon size={26} />
            </div>
            <div>
              <h2 id="wpp-modal-title" className={styles.title}>
                Soporte Oficial por WhatsApp
              </h2>
              <p className={styles.subtitle}>
                Atención directa con nuestro equipo zootécnico y técnico en tiempo real.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Cerrar modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tarjeta de Línea de Atención */}
        <div className={styles.agentCard}>
          <div className={styles.agentInfo}>
            <div className={styles.statusRow}>
              <span className={styles.statusDot} />
              <span>Canal Oficial de Asistencia</span>
            </div>
            <span className={styles.phoneNumber}>{numeroDisplay}</span>
          </div>
          <button
            type="button"
            onClick={handleCopyNumber}
            className={styles.copyNumberBtn}
            title="Copiar número"
          >
            {copied ? (
              <>
                <Check size={14} className="text-green-600" />
                <span>Copiado</span>
              </>
            ) : (
              <>
                <Copy size={14} />
                <span>Copiar</span>
              </>
            )}
          </button>
        </div>

        {/* Sección de Mensaje */}
        <div className={styles.messageSection}>
          <label htmlFor="wpp-msg-area" className={styles.messageLabel}>
            Mensaje para iniciar la conversación:
          </label>
          <textarea
            id="wpp-msg-area"
            rows={3}
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder="Escribe tu consulta aquí..."
            className={styles.messageArea}
          />
        </div>

        {/* Botón CTA Iniciar Chat */}
        <a
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.startChatBtn}
          onClick={onClose}
        >
          <WhatsAppIcon size={20} color="#FFFFFF" />
          <span>Abrir Conversación en WhatsApp</span>
          <ExternalLink size={16} />
        </a>

        <p className={styles.disclaimer}>
          Horario de atención: Lunes a Sábado de 6:00 AM a 8:00 PM (Hora Colombia).
        </p>
      </div>
    </div>,
    document.body
  );
}
